import axios from 'axios';
import securedAxios, { newSecuredAxios } from '../../utils/secured-axios';
import { MerchantDetail } from '../../interfaces/merchants';
import { MerchantReviewsResponse } from '../../interfaces/merchants/reviews';
import {
  MerchantProduct,
  MerchantProductIndex,
} from '../../interfaces/merchants/products';
import { MerchantBlogIndex } from '../../interfaces/merchants/blogs';
import User, { UserRole } from '../../domain/entities/User';
import { coubicLocale } from '../../utils/i18n';
import {
  buildApiV2Url,
  buildUrl,
  redirectIfHostChanged,
} from 'src/utils/request-url';
import { Redirect } from 'next';

// FIXME: skipHostCheck = true なら返値の型をunionの1つ目のみに限定できるはず
export const fetchMerchantDetail = async ({
  session,
  token,
  requestId,
  merchantPublicId,
  skipHostCheck,
}: {
  session: string;
  token: string;
  requestId: string;
  merchantPublicId: string;
  skipHostCheck?: boolean;
}): Promise<
  | { merchant?: MerchantDetail; isUnpublishedSite: boolean; redirect?: never }
  | { redirect: Redirect }
> => {
  const client = newSecuredAxios({ session, token, requestId });
  try {
    const res = await client.get<MerchantDetail | undefined>(
      buildApiV2Url(`/merchants/${merchantPublicId}`),
    );
    const merchant = res.status === 200 ? res.data : undefined;
    const siteUnpublishedHeader = res.headers['x-coubic-site-unpublished'];
    const isUnpublishedSite = siteUnpublishedHeader
      ? Boolean(siteUnpublishedHeader)
      : false;

    if (merchant && !skipHostCheck) {
      const redirect = redirectIfHostChanged(merchant);
      if (redirect) return redirect;
    }

    return {
      merchant,
      isUnpublishedSite,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      // axiosで4xx系はthrowしないようにした方が良いが、影響範囲が大きいのでerrorからresponse headerを取り出している
      const siteUnpublishedHeader =
        error.response?.headers['x-coubic-site-unpublished'];
      const isUnpublishedSite = siteUnpublishedHeader
        ? Boolean(siteUnpublishedHeader)
        : false;
      return {
        merchant: undefined,
        isUnpublishedSite,
      };
    } else {
      throw error;
    }
  }
};

export const fetchMerchantReviewsForSSR = async ({
  session,
  token,
  requestId,
  merchantPublicId,
  page,
  perPage,
}: {
  session: string;
  token: string;
  requestId: string;
  merchantPublicId: string;
  page: number;
  perPage: number;
}) => {
  const client = newSecuredAxios({ session, token, requestId });
  try {
    const { data } = await client.get<MerchantReviewsResponse>(
      buildApiV2Url(
        `/merchants/${merchantPublicId}/reviews?page=${page}&per_page=${perPage}`,
      ),
    );

    return data;
  } catch (error) {
    return undefined;
  }
};

export const fetchMerchantReviews = async (
  merchantPublicId: string,
  page: number,
  perPage: number,
) => {
  const { data } = await securedAxios.get<MerchantReviewsResponse>(
    buildApiV2Url(
      `/merchants/${merchantPublicId}/reviews?page=${page}&per_page=${perPage}`,
    ),
  );

  return data;
};

export const fetchMerchantProducts = async ({
  session,
  token,
  requestId,
  merchantPublicId,
  page,
  perPage,
}: {
  session: string;
  token: string;
  requestId: string;
  merchantPublicId: string;
  page: number;
  perPage: number;
}) => {
  const client = newSecuredAxios({ session, token, requestId });
  const { data } = await client.get<MerchantProductIndex>(
    buildApiV2Url(
      `/merchants/${merchantPublicId}/products?page=${page}&per_page=${perPage}`,
    ),
  );

  return data;
};

export const fetchProductDetail = async ({
  session,
  token,
  requestId,
  merchantPublicId,
  productPublicId,
}: {
  session: string;
  token: string;
  requestId: string;
  merchantPublicId: string;
  productPublicId: string;
}) => {
  const client = newSecuredAxios({ session, token, requestId });
  const { data } = await client.get<MerchantProduct>(
    buildApiV2Url(`/merchants/${merchantPublicId}/products/${productPublicId}`),
  );

  return data;
};

export const fetchMerchantBlogs = async ({
  session,
  token,
  requestId,
  merchantPublicId,
  blogPageNumber,
}: {
  session: string;
  token: string;
  requestId: string;
  merchantPublicId: string;
  blogPageNumber: string;
}) => {
  const client = newSecuredAxios({ session, token, requestId });
  const { data } = await client.get<MerchantBlogIndex>(
    buildApiV2Url(
      `/merchants/${merchantPublicId}/posts?page=${blogPageNumber}`,
    ),
  );

  return data;
};

export type PublicStaff = {
  canonical_id: string;
  name: string;
  image_url: string;
  sort_order: number;
  updated_at: number;
};

type VoucherAssignedResource = {
  public_id: string;
  name: string;
};

export type BookingCalendarMeta = {
  public_id: string;
  name: string;
  assigned_staff_members: PublicStaff[];
  assigned_vouchers: VoucherAssignedResource[];
};

export interface MeResponse {
  credit_card?: {
    canonical_id: string;
    tail_digits: string;
    expire_month: string;
    expire_year: string;
  };
  display_name: string;
  email: string;
  firstname: string;
  id: number;
  is_email_verified: boolean;
  lastname: string;
  phone_number?: string;
  public_id: string;
  thumbnail: {
    c120x120: { url: string };
    p1280x1280: { url: string };
    square: { url: string };
    thumb: { url: string };
  };
  role: UserRole;
  locale: coubicLocale;
  supervisor_proxy_signed_in: boolean;
}

export const fetchMeUser = async () => {
  try {
    const res = await securedAxios.get<MeResponse>(buildApiV2Url('/users/me'));
    const me = res.data;
    // UserContextのUser型に合わせる
    return new User({
      id: me.id,
      displayName: me.display_name,
      publicId: me.public_id,
      thumbnailUrl: {
        c120x120: me.thumbnail.c120x120.url,
        p1280x1280: me.thumbnail.p1280x1280.url,
        square: me.thumbnail.square.url,
        thumb: me.thumbnail.thumb.url,
      },
      email: me.email,
      role: me.role,
    });
  } catch (error) {
    return undefined;
  }
};

export const fetchCsrfToken = async () => {
  const { data } = await securedAxios.get<{ csrfToken: string }>(
    buildUrl('/_/csrf'),
  );
  return data.csrfToken;
};
